import React from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useAuth } from 'oidc-react';
import { usePermissions } from '@/hooks/usePermissions';
import { UserRole } from '@/types';

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
  requiredRole?: UserRole;
  requiredRoles?: UserRole[];
}

const ProtectedAdminRoute: React.FC<ProtectedAdminRouteProps> = ({
  children,
  requiredPermission,
  requiredRole,
  requiredRoles,
}) => {
  const { userData, isLoading } = useAuth();
  const { hasPermission, hasRole } = usePermissions();

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh"
        }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  // With OIDC autoSignIn, users are automatically redirected if not authenticated
  // Just check if userData exists
  if (!userData) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh"
        }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  // Check if user has admin role (B2B or System Admin)
  const isAdminRole = hasRole(UserRole.AGROKOLL_B2B) ||
                     hasRole(UserRole.AGROKOLL_SYSTEM_ADMIN);

  if (!isAdminRole) {
    return (
      <Box sx={{
        p: 3
      }}>
        <Alert severity="error">
          Du har inte behörighet att komma åt denna sida. Endast administratörer har åtkomst.
        </Alert>
      </Box>
    );
  }

  // Check specific role requirement
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <Box sx={{
        p: 3
      }}>
        <Alert severity="error">
          Du har inte tillräckliga behörigheter för denna sida. Krävs: {requiredRole}
        </Alert>
      </Box>
    );
  }

  // Check if user has any of the required roles
  if (requiredRoles && !requiredRoles.some(role => hasRole(role))) {
    return (
      <Box sx={{
        p: 3
      }}>
        <Alert severity="error">
          Du har inte tillräckliga behörigheter för denna sida. Krävs en av: {requiredRoles.join(', ')}
        </Alert>
      </Box>
    );
  }

  // Check specific permission requirement
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <Box sx={{
        p: 3
      }}>
        <Alert severity="error">
          Du har inte den behörighet som krävs för denna sida.
        </Alert>
      </Box>
    );
  }

  // All checks passed, render children
  return <>{children}</>;
};

export default ProtectedAdminRoute;